import React from 'react';
import { withStyles } from '@material-ui/core';
import {
    Cancel as CancelIcon, CheckCircle
} from '@markinson/uicomponents-v2/SvgIcons/';
import FormView from 'components/FormView';
import CreditNotesForm from './Notes.from';
import styles from './Notes.styles';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import INotesProperties, { INotesHandle } from './Notes.properties';
import { useCNContextSelector } from '../CreditNotes.context';
import { useFetchCreditNoteNotes, useUpdateCreditNoteNotes } from 'api/creditNotes/creditNotes';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';

const Notes = (props: INotesProperties, ref: React.Ref<INotesHandle>) => {
    const { classes, changeSelectedTab, values, resetForm, dirty, changeConfirmationDialog } = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const CreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const { data: creditNoteNotes, isLoading: isCreditNoteNotesLoading, isFetching: isCreditNoteNotesFetching } = useFetchCreditNoteNotes(CreditNoteId);
    const useUpdateCreditNoteNotesMutation = useUpdateCreditNoteNotes();

    const onOk = React.useCallback(
        async () => {
            const response = await updateCreditNoteNotes(values);
            if (response?.Status) {
                resetForm();
                changeSelectedTab('Lines');

                return;
            }
        },
        [values]
    );

    const updateCreditNoteNotes = async (data: any) => {
        const response = await useUpdateCreditNoteNotesMutation.mutateAsync({ WorkCreditNoteId: CreditNoteId, ...data });

        return response;
    };

    const onCancel = React.useCallback(
        async () => {
            if (dirty) {
                changeConfirmationDialog({
                    open: true,
                    title: 'Discard changes',
                    message: 'Are you sure you want to continue?',
                    okLabel: 'Discard',
                    onCancel: () => undefined,
                    onOk: () => {
                        resetForm();
                        setTimeout(
                            () => {
                                changeSelectedTab('Lines');
                            },
                            0);
                    }
                });
            } else {
                resetForm();
                changeSelectedTab('Lines');
            }

        },
        [values]
    );

    const rightIcons = React.useMemo(
        () => [
            {
                label: 'Cancel',
                Icon: CancelIcon,
                iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                action: 'CreditNoteDetailsCancel',
                callBack: onCancel
            },
            {
                label: 'OK',
                Icon: CheckCircle,
                action: 'CreditNoteDetailsOk',
                callBack: onOk
            },
        ],
        [onCancel, onOk]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            CreditNoteDetailsOk: onOk,
            CreditNoteDetailsCancel: onCancel,
            isChangesSavedOrDiscarded(): boolean {
                if (dirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !dirty;
            },
        }),
        [onOk, onCancel]
    );

    React.useEffect(
        () => {
            setActionBar({
                leftIcons: [],
                centerIcons: [],
                rightIcons
            });
        },
        [rightIcons]
    );

    return (<div className={classes.formContainer}>
        <FormView
            isLoading={isCreditNoteNotesLoading || isCreditNoteNotesFetching}
            formName={'CreditNotesForm'}
            schema={CreditNotesForm}
            includeTabsHeight={false}
            initialValues={creditNoteNotes?.Notes?.inlineObject}
            valuesSchema={creditNoteNotes?.Notes?.schema}
            operationMode={'EDIT'}
        />
    </div>);
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Notes)));
